/**
 * VSCode Live Server serves unknown file types (including `.pagefind`) with `Content-Type: text/html`,
 * which breaks `WebAssembly.instantiateStreaming()` even if the response body is valid wasm bytes.
 *
 * Pagefind uses `wasm.*.pagefind`, so we patch `instantiateStreaming` to fall back to ArrayBuffer
 * instantiation when streaming fails.
 *
 * This is safe to run multiple times; it will only patch once.
 */
export function installWasmInstantiateStreamingFallback(): void {
	type InstantiateStreaming = (
		source: Response | Promise<Response>,
		importObject?: WebAssembly.Imports,
	) => Promise<WebAssembly.WebAssemblyInstantiatedSource>;

	const wasm = WebAssembly as unknown as {
		instantiateStreaming?: InstantiateStreaming;
		instantiate: (
			bufferSource: BufferSource,
			importObject?: WebAssembly.Imports,
		) => Promise<WebAssembly.WebAssemblyInstantiatedSource>;
		__pagefindPatchedInstantiateStreaming?: boolean;
	};

	if (!wasm.instantiateStreaming) return;
	if (wasm.__pagefindPatchedInstantiateStreaming) return;
	wasm.__pagefindPatchedInstantiateStreaming = true;

	const original = wasm.instantiateStreaming;

	wasm.instantiateStreaming = async (
		source: Response | Promise<Response>,
		importObject?: WebAssembly.Imports,
	) => {
		try {
			return await original(source, importObject);
		} catch (err) {
			const msg = err instanceof Error ? err.message : String(err);
			console.warn(
				"[pagefind] WebAssembly.instantiateStreaming failed; falling back to ArrayBuffer",
				msg,
			);

			const res = await Promise.resolve(source);
			const ab = await res.arrayBuffer();
			return await WebAssembly.instantiate(ab, importObject);
		}
	};
}
